<template>
  <div class="mb-2">
    <AppFilter :filters="filterConfig" :filter-submitting="filterSubmitting" @filter="handleFilter">
      <template #extra-actions>
        <b-button
          id="default-filter-new-btn"
          variant="primary"
          type="button"
          @click="downloadDocument()"
        >
          <feather-icon icon="DownloadIcon" size="15" />
          <b-tooltip
            triggers="hover"
            target="default-filter-new-btn"
            :title="t('Download Document')"
            :delay="{ show: 0, hide: 50 }"
          />
        </b-button>
        <b-button
          id="default-filter-import-btn"
          variant="primary"
          type="button"
          @click="importDocumentModal()"
        >
          <feather-icon icon="UploadIcon" size="15" />
          <b-tooltip
            triggers="hover"
            target="default-filter-import-btn"
            :title="t('Import Document')"
            :delay="{ show: 0, hide: 50 }"
          />
        </b-button>
      </template>
    </AppFilter>
    <b-modal
      id="document-export-modal"
      ref="document-export-modal"
      size="sm"
      :title="t('Download Document')"
      :ok-title="t('Cancel')"
      ok-only
      @ok="handleCancel"
    >
      <label class="mb-1">{{ t('Downloading...') }}</label>
      <b-progress :value="exportProgress" :max="100" show-progress animated />
      <b-button v-if="exportProgress == 100" class="mt-2 text-sm" @click="handleFile()">
        {{ t('Download Document') }}
      </b-button>
    </b-modal>

    <b-modal
      id="invoice-upload-modal"
      ref="invoice-upload-modal"
      cancel-variant="outline-secondary"
      :ok-title="t('Save')"
      :cancel-title="progressShow == false ? t('Close') : t('Cancel')"
      centered
      :title="t('Import Document')"
      size="sm"
      :ok-disabled="submitting"
      @ok="handleOk"
      @cancel="handleCancel"
    >
      <validation-observer ref="invoiceUploadDocumentForm">
        <b-form>
          <b-row>
            <b-col sm="12">
              <b-form-group v-if="!progressShow">
                <div class="d-flex form-group-label">
                  <label for="name">{{ t('Document') }}</label>
                  <span class="text-danger">*</span>
                </div>
                <validation-provider #default="{ errors }" :name="t('Document')" rules="required">
                  <b-form-file
                    v-model="files"
                    :placeholder="t('Choose a file or drop it here')"
                    :drop-placeholder="t('Drop file here')"
                    multiple
                    :browse-text="t('Select File')"
                    accept=".xlsx"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>

              <b-col v-else>
                <div
                  v-if="spinning"
                  class="d-flex flex-column align-items-center justify-content-center my-3"
                >
                  <b-spinner label="Spinning" />
                  <b-badge class="mb-1 mt-2">
                    {{ t('Importing Start Info') }}
                  </b-badge>
                </div>

                <div v-else>
                  <label class="mb-1">{{ t('Importing...') }}</label>
                  <b-progress
                    :value="importProgress"
                    :max="totalProgressValue"
                    show-progress
                    animated
                  />
                  <b-badge pill variant="primary" class="mb-1 mt-2">
                    {{ t('Importing Cancel Info') }}
                  </b-badge>
                </div>
              </b-col>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
  </div>
</template>
<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { mapGetters } from 'vuex';
import {
  BButton,
  BBadge,
  BForm,
  BFormFile,
  BFormGroup,
  BTooltip,
  BRow,
  BCol,
  BModal,
  BProgress,
  BSpinner
} from 'bootstrap-vue';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import AppFilter from '@/components/AppFilter/AppFilter.vue';

export default {
  components: {
    ValidationProvider,
    ValidationObserver,

    BButton,
    BBadge,
    BForm,
    BFormFile,
    BFormGroup,
    BTooltip,
    BRow,
    BCol,
    BModal,
    BProgress,
    BSpinner,
    AppFilter
  },

  mixins: [helper],
  props: {
    filterSubmitting: {
      type: Boolean,
      required: true,
      default: false
    }
  },
  data() {
    return {
      values: {
        customer: '',
        from: '',
        to: ''
      },
      submitting: false,
      files: null,
      formData: null,
      progressShow: false,
      totalProgressValue: 0,
      spinning: false,
      customers: []
    };
  },

  computed: {
    ...mapGetters({
      exportProgress: 'app/exportProgress',
      exportFile: 'app/exportFile',
      importProgress: 'app/importProgress',
      importFile: 'app/importFile'
    }),
    customerOptions() {
      return (this.customers || []).map(item => ({
        value: item.id,
        label: item.name
      }));
    },
    filterConfig() {
      return [
        {
          id: 'customer',
          label: 'Customer',
          type: 'select',
          options: this.customerOptions,
          placeholder: 'Make a Choice'
        },
        {
          id: 'from',
          label: 'Start Date',
          type: 'date',
          placeholder: 'Select Date'
        },
        {
          id: 'to',
          label: 'End Date',
          type: 'date',
          placeholder: 'Select Date'
        }
      ];
    }
  },

  async created() {
    this.customers = await this.getCustomers({ paginated: false });
  },

  methods: {
    handleFilter(filters) {
      this.$emit('filter', filters);
      this.values = filters;
    },
    downloadDocument() {
      const requestData = {
        name: 'ptt-consignments',
        date_between: {}
      };

      if (this.values.customer) {
        requestData.customer_id = this.values.customer;
      } else {
        this.showErrorMessage(this.$t('Select Customer Warning'));
        return;
      }

      if (this.values.from) {
        requestData.date_between.from = this.values.from;
      } else {
        this.showErrorMessage(this.$t('Start Date Warning'));
        return;
      }

      if (this.values.to) {
        requestData.date_between.to = this.values.to;
      } else {
        this.showErrorMessage(this.$t('End Date Warning'));
        return;
      }

      this.exportDocument(requestData).then(response => {
        this.$refs['document-export-modal'].show();
        this.documentExportControl(response);
      });
    },
    handleFile() {
      const url = window.URL.createObjectURL(new Blob([this.exportFile]), {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      this.$refs['document-export-modal'].hide();
    },
    resetModal() {
      this.files = null;
    },
    handleSubmit() {
      this.$refs.invoiceUploadDocumentForm.validate().then(success => {
        if (success) {
          const formData = new FormData();

          this.files.forEach(file => {
            formData.append('file', file);
          });

          this.spinning = true;
          this.progressShow = true;
          this.importDocument(formData).then(response => {
            this.spinning = false;
            this.totalProgressValue = response.data.totalRowCount;
            this.progressShow = true;
            this.submitting = true;
            this.importDocumentProgress(response.data.id);
          });
        }
      });
    },
    formatNames(files) {
      return files.length === 1 ? files[0].name : `${files.length} files selected`;
    },
    importDocumentModal() {
      this.$refs['invoice-upload-modal'].show();
    },
    handleOk(bvModalEvent) {
      if (this.submitting) return;

      bvModalEvent.preventDefault();
      this.handleSubmit();
    },
    handleCancel() {
      this.stopImportInterval();
      this.progressShow = false;
      this.submitting = false;
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
